import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomHeader } from "../components/AuthHeader";
import { CustomButton } from "../components/CustomButton";
import { useAuthStore } from "../features/auth/authStore";
import { useToast } from "../components/ToastProvider";
import { ArrowLeft, Eye, EyeOff, ChevronRight } from "lucide-react";
import rushLogo from "../assets/png/rush-logo.png";

export default function LoginScreen() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { login, isLoading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleLogin = async () => {
    if (!email || !password) {
      showToast("Please enter email and password", "error");
      return;
    }

    try {
      await login({ email, password });
      showToast("Login successful!", "success");
      navigate("/home");
    } catch (error: any) {
      showToast(error.message || "Login failed", "error");
    }
  };

  return (
    <div className="flex flex-col bg-white px-6 pt-4 pb-10">
      {/* Sticky Header with Back Button and Logo */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-md px-6 -mx-6 pt-4 pb-4 flex items-center justify-between mb-4">
        <button
          onClick={() => navigate("/auth")}
          className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center active:scale-95 transition-transform shadow-sm bg-white"
        >
          <ArrowLeft size={20} className="text-gray-700" />
        </button>

        <img
          src={rushLogo}
          alt="Rush Logo"
          className="h-7 w-auto object-contain"
        />
      </div>

      {/* Welcome Header */}
      <CustomHeader
        title="Welcome Back"
        subtitle="Sign in to place new orders and track your laundry."
      />

      {/* Main Content Area (Form) - Contains all inputs and primary buttons */}
      <div className="mt-5 space-y-5">
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block">
            Email Address
          </label>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            className="w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all disabled:opacity-50"
          />
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className="w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all disabled:opacity-50"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
              onClick={() => setShowPassword((v) => !v)}
              aria-label="Toggle password visibility"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="flex justify-end mt-3">
            <button
              onClick={() => navigate("/forgot-password")}
              className="text-[#23A9C6] text-sm font-bold flex items-center gap-1 hover:underline"
            >
              Forgot Password? <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Divider and Alternative Login */}
        {/* <div className="pt-2">
          <div className="relative flex py-1 items-center">
            <div className="flex-grow border-t border-gray-100"></div>
            <span className="flex-shrink mx-4 text-gray-400 text-xs font-bold uppercase tracking-widest">
              Or Continue With
            </span>
            <div className="flex-grow border-t border-gray-100"></div>
          </div>

          <button
            onClick={() => navigate("/login/phone")}
            className="w-full h-14 border border-gray-100 rounded-2xl flex items-center justify-center gap-3 text-gray-700 font-bold hover:bg-gray-50 transition-colors active:scale-[0.98]"
          >
            <span>Mobile Number</span>
          </button>
        </div> */}

        <div className="mt-10 mb-4 text-center text-sm text-gray-500 animate-in fade-in slide-in-from-bottom-2">
          Don’t have an account?{" "}
          <button
            onClick={() => navigate("/signup")}
            className="text-[#23A9C6] font-bold transition-all hover:text-[#1A7C92]"
          >
            Sign up
          </button>
        </div>

        <CustomButton
          variant="primary"
          disabled={isLoading}
          onClick={handleLogin}
          className="w-full h-14 text-lg rounded-2xl font-bold flex items-center justify-center gap-3 active:scale-[0.98] transition-transform shadow-lg shadow-cyan-100"
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Logging in...</span>
            </>
          ) : (
            <span>Log In</span>
          )}
        </CustomButton>
      </div>
    </div>
  );
}
